import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import {
  stageOffersFeedbackCall,
  type FeedbackCallConfig,
} from '../../services/interviewLinksApi';
import { InterviewerEditor } from './InterviewerEditor';

/**
 * The stage's post-rejection feedback call.
 *
 * A candidate rejected after a case or final round gets a rejection email
 * offering a 15-minute feedback call — this section configures who takes that
 * call and when they are bookable. No stage field: the link already IS one
 * role+stage, so the call inherits it.
 *
 * The default follows the rejection-email rules (LinkForm applies it on stage
 * pick): case/final stages offer the call, so the toggle starts on there; a
 * first-interview rejection never offers one — its email invites a reply
 * instead — so the toggle starts off and the hint says why.
 */
export function FeedbackCallEditor({
  value,
  stageId,
  onChange,
}: {
  value: FeedbackCallConfig | null;
  stageId: string;
  onChange: (next: FeedbackCallConfig | null) => void;
}) {
  const enabled = value?.enabled === true;
  const interviewers = value?.interviewers ?? [];
  const stagePicked = stageId.trim().length > 0;
  const stageOffersCall = stageOffersFeedbackCall(stageId);

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-gray-900">Feedback call (after a rejection)</h2>
      <p className="mt-1 text-xs text-gray-500">
        Candidates rejected at this stage are offered a 15-minute feedback call in their rejection
        email. Configure who takes that call and when they are bookable — the email then links our
        own booking page instead of the booking link from the recruiter overview sheet.
      </p>

      <div className="mt-3">
        <Checkbox
          label="Offer a 15-min feedback call at this stage"
          checked={enabled}
          onCheckedChange={(checked) =>
            onChange(
              checked === true
                ? { enabled: true, interviewers }
                : // Keep the people and their availability — re-enabling restores them.
                  interviewers.length > 0
                  ? { enabled: false, interviewers }
                  : null
            )
          }
        />
        {stagePicked && !stageOffersCall ? (
          <p className="ml-6 mt-0.5 text-xs text-gray-500">
            Rejections at this stage never offer a feedback call — the email invites the candidate
            to reply instead — so this is off by default here.
          </p>
        ) : null}
        {!stagePicked ? (
          <p className="ml-6 mt-0.5 text-xs text-gray-500">
            Pick an interview stage first — case and final stages offer the call by default.
          </p>
        ) : null}
      </div>

      {enabled ? (
        <div className="mt-4">
          <p className="mb-1 text-xs font-medium text-gray-700">
            Who takes the call <span className="text-red-600">*</span>
          </p>
          <InterviewerEditor
            meetingType="1:1"
            interviewers={interviewers}
            onChange={(next) => onChange({ enabled: true, interviewers: next })}
            embedded
          />
        </div>
      ) : null}
    </section>
  );
}
